<template>
  <div class="participant-screeners--wrap">
    <div class="participant-screeners">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <div class="container" v-if="!screener">
        <v-row>
          <v-col class="text-right">
            <v-btn
              color="accent"
              elevation="0"
              rounded
              @click="openScheduleScreenerDialog"
            >
              Schedule screener
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <main-user-table
              :headers="headers"
              :items="screeners"
              :items-per-page="queryParams.pageSize"
              :server-items-length="pagination.totalCount || -1"
              :page="queryParams.page"
              :row-component="'screeners-detailed-table-row'"
              @to-screener="toScreener"
              @action:get-data="getDataAction"
            >
            </main-user-table>
          </v-col>
        </v-row>
      </div>
      <screener-content
        v-else
        :screener="screener"
        :screener-progress="screenerProgress"
        @back="back"
        @toScreener="toScreener"
      />
      <v-dialog
        v-model="showScheduleScreenerDialog"
        persistent
        max-width="490"
        content-class="schedule-screener-dialog"
        @click:outside="onClickOutsideDialog"
        @keydown="onKeydownDialog"
      >
        <v-card>
          <v-card-title class="headline pb-6">Schedule Screener</v-card-title>
          <v-card-text>
            <v-row>
              <v-col>
                <dialog-select
                  label="Select screener"
                  name="screenerDefinitionId"
                  :items="screenerTemplates"
                  :value="
                    screenerTemplates && screenerTemplates.length > 0
                      ? screenerTemplates[0]
                      : null
                  "
                  item-text="title"
                  item-value="id"
                  @input="onInput"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col col="6">
                <dialog-date-picker
                  label="Date"
                  name="date"
                  :value="scheduleData.date | moment('MM/DD/YYYY')"
                  @input="onInput"
                />
              </v-col>
              <v-col col="6">
                <dialog-time-picker
                  label="Time"
                  name="time"
                  :initialTime="scheduleData.time | moment('hh:mm A')"
                  @input="onInput"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <dialog-input
                  useNumbers
                  label="Expires in (days)"
                  :value="scheduleData.expiration"
                  name="expiration"
                  @input="onInput"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="rounded-pill" text @click="closeDialog">Cancel</v-btn>
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="scheduleScreenerTemplate"
              >Schedule</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import { debounce } from '@/helpers/debounce'
import RouterMixin from '@/mixins/router'
import MainUserTable from '@/components/tables/MainUserTable.vue'
import ScreenerContent from '@/components/screeners/ScreenerContent.vue'
import DialogSelect from '@/components/forms/DialogSelect.vue'
import DialogDatePicker from '@/components/forms/DialogDatePicker.vue'
import DialogTimePicker from '@/components/forms/DialogTimePicker.vue'
import DialogInput from '@/components/forms/DialogInput.vue'

const today = new Date()
// @ is an alias to /src
export default {
  name: 'ParticipantScreener',
  components: {
    MainUserTable,
    ScreenerContent,
    DialogSelect,
    DialogDatePicker,
    DialogTimePicker,
    DialogInput,
  },
  mixins: [RouterMixin],
  data() {
    return {
      headers: [
        { text: 'Title', value: 'title', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Sent', value: 'creationDate', sortable: false },
        { text: 'Open date', value: 'startDate', sortable: false },
        { text: 'Overall score', value: 'score', sortable: false },
        { sortable: false },
      ],
      selected: null,
      showScheduleScreenerDialog: false,
      scheduleData: {
        date: this.$moment(today).format('YYYY-MM-DD'),
        time: '12',
        expiration: 5,
        screenerDefinitionId: null,
      },
    }
  },
  computed: {
    ...mapState('patientsStates', [
      'currentPatient',
      'screener',
      'screenerProgress',
      'screeners',
      'pagination',
      'loading',
    ]),
    ...mapState('screenersStates', ['screenerTemplates']),
  },
  mounted() {
    this.ready = true
    if (this.currentPatient?.id)
      this.getScreeners({ participantId: this.currentPatient.id })
    if (this.$route.params.id)
      this.getScreeners({ participantId: this.$route.params.id })
  },
  created() {},
  watch: {
    currentPatient(patient) {
      if (patient?.id) this.getScreeners({ participantId: patient.id })
    },
    $route(to, from) {
      // get new current patient data when patient id changed
      if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
        this.getPatient(to.params.id)
      }
    },
  },
  methods: {
    ...mapActions('patientsStates', [
      'getScreeners',
      'getScreener',
      'getScreenerProgress',
      'getPatient',
    ]),
    ...mapMutations('patientsStates', ['setScreener', 'setScreenerProgress']),
    ...mapActions('screenersStates', [
      'getScreenerTemplates',
      'scheduleScreenerToParticipants',
    ]),
    toScreener(id) {
      debounce(() => {
        this.getScreener({
          participantId: this.currentPatient.id,
          screenerId: id,
        }).then(screener => {
          this.selected = screener.id
        })
        this.getScreenerProgress({
          participantId: this.currentPatient.id,
          screenerId: id,
        })
      }, 100)
    },
    back() {
      this.setScreener(null)
      this.setScreenerProgress(null)
    },
    openScheduleScreenerDialog() {
      this.getScreenerTemplates({}).then(() => {
        this.showScheduleScreenerDialog = true
        if (this.screenerTemplates && this.screenerTemplates.length > 0) {
          this.scheduleData[
            'screenerDefinitionId'
          ] = this.screenerTemplates[0].id
        }
      })
    },
    closeDialog() {
      this.showScheduleScreenerDialog = false
    },
    onClickOutsideDialog() {
      this.closeDialog()
    },
    onKeydownDialog(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    scheduleScreenerTemplate() {
      const tmpDateTime = this.$moment(
        this.scheduleData.date + ' ' + this.scheduleData.time,
      ).format('YYYY-MM-DDTHH:mm')
      const data = {
        date: tmpDateTime,
        expiration: this.scheduleData.expiration,
        surveyDefinitionId: this.scheduleData.screenerDefinitionId,
        participants: [this.currentPatient.id],
      }
      this.scheduleScreenerToParticipants(data).then(() => {
        this.closeDialog()
        this.getScreeners({ participantId: this.currentPatient.id })
      })
    },
    onInput({ name, value }) {
      this.scheduleData[name] = value
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.participant-screeners {
  flex: 1;
  padding: 24px 10px 0;
  height: 100%;
  h3 {
    color: $bh-gray;
    margin-bottom: 10px;
    padding-right: 80px;
    small {
      margin-left: 8px;
      font-size: 0.6em;
      font-weight: 400;
    }
  }
  .v-chip-group {
    margin-bottom: 30px;
    .v-slide-group__content {
      background: $bh-gray--6;
      flex: 0;
      padding-left: 8px;
      border-radius: 20px;
    }
  }
  h2 {
    color: $bh-gray--2;
    font-size: 14px;
  }
  &--wrap {
    height: 100%;
  }
  &--not-found {
    h2 {
      margin-top: 30px;
    }
  }
  & > .container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: 0;
    & > .row > .col {
      padding-bottom: 0;
    }
  }
  .screener-section {
    h4 {
      color: $bh-black;
      margin-bottom: 0;
    }
    table {
      border-spacing: 0 15px;
      td {
        vertical-align: top;
      }
      th {
        text-align: left;
        &:last-of-type {
          text-align: right;
        }
      }
      td:first-of-type {
        width: 64%;
      }
      td:nth-of-type(3) {
        width: 8%;
        font-size: 1.5em;
        font-weight: bold;
        text-align: right;
        i {
          height: 24px;
          display: inline-block;
        }
      }
    }
    &__score {
      width: 200px;
      position: relative;
      flex-grow: 0;
      margin-right: 40px;
      .horizontal-value-bar {
        background: $bh-blue--4;
        &__wrapper {
          padding-top: 8px;
        }
        &__details {
          label {
            display: none;
          }
          p {
            position: absolute;
            right: 0;
            top: 0;
            strong {
              font-size: 1.5em;
            }
          }
        }
      }
    }
    .v-expansion-panel-content {
      table {
      }
    }
  }
}
.schedule-screener-dialog {
  .v-card {
    &__text,
    &__actions {
      background: $bh-gray--6;
    }
  }
}
</style>
